import { apiFetch } from './apiClient.js';

export type SurveyStatus = 'active' | 'paused' | 'archived';

export interface Survey {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  flowId: string;
  channelId?: string;
  status: SurveyStatus;
  questionsCount: number;
  responsesCount: number;
  averageScore?: number;
  startsAt?: string;
  endsAt?: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface SurveyCreate {
  name: string;
  description?: string;
  flowId: string;
  channelId?: string;
  status?: SurveyStatus;
  questionsCount?: number;
  responsesCount?: number;
  averageScore?: number;
  startsAt?: string;
  endsAt?: string;
}

export type SurveyPatch = Partial<
  Pick<
    Survey,
    | 'name'
    | 'description'
    | 'flowId'
    | 'channelId'
    | 'status'
    | 'questionsCount'
    | 'responsesCount'
    | 'averageScore'
    | 'startsAt'
    | 'endsAt'
  >
>;

/** Regex local mirror del flowId aceptado por backend. */
export const FLOW_ID_REGEX = /^[a-z0-9-]+$/;

interface ListSurveysResponse {
  items: Survey[];
}

export async function listSurveys(
  tenantId: string,
  opts: { includeArchived?: boolean; status?: SurveyStatus } = {},
): Promise<Survey[]> {
  const params: string[] = [];
  if (opts.includeArchived) params.push('includeArchived=true');
  if (opts.status) params.push(`status=${encodeURIComponent(opts.status)}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  const res = await apiFetch<ListSurveysResponse | Survey[]>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/surveys${qs}`,
  );
  return Array.isArray(res) ? res : res.items;
}

export async function getSurvey(tenantId: string, surveyId: string): Promise<Survey> {
  return apiFetch<Survey>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/surveys/${encodeURIComponent(surveyId)}`,
  );
}

export async function createSurvey(
  tenantId: string,
  body: SurveyCreate,
): Promise<Survey> {
  return apiFetch<Survey>(`/v1/tenants/${encodeURIComponent(tenantId)}/surveys`, {
    method: 'POST',
    body,
  });
}

export async function updateSurvey(
  tenantId: string,
  surveyId: string,
  patch: SurveyPatch,
  ifMatchVersion: number,
): Promise<Survey> {
  return apiFetch<Survey>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/surveys/${encodeURIComponent(surveyId)}`,
    {
      method: 'PATCH',
      body: patch,
      headers: { 'If-Match': String(ifMatchVersion) },
    },
  );
}

export async function archiveSurvey(tenantId: string, surveyId: string): Promise<Survey> {
  return apiFetch<Survey>(
    `/v1/tenants/${encodeURIComponent(tenantId)}/surveys/${encodeURIComponent(surveyId)}/archive`,
    { method: 'POST' },
  );
}
